import React, { useCallback, useEffect, useRef, useState } from "react";
import { Binoculars, GitCompare, Minus, Plus } from "lucide-react";
import { useApi } from "../hooks/useApi";
import PackDetailModal from "./PackDetailModal";

// Poll cadence for the publications metadata (default 10s).
const REFRESH_MS = 10000;

// A publication pack is uniquely identified by (type, business_date,
// publication_ts_utc). publication_ts_utc alone is the canonical id used as a
// query param downstream; the other two are carried for convenience.
const pubKey = (p) =>
  p ? `${p.type}|${p.business_date}|${p.publication_ts_utc}` : null;

const samePub = (a, b) => Boolean(a) && Boolean(b) && pubKey(a) === pubKey(b);

// Publications arrive newest-first, so the first match is the latest pack of
// that type FOR THE CHOSEN DELIVERY DATE. Auto-selection is scoped to the
// delivery date so packs already released for a later date are never picked.
const newestForDate = (pubs, type, date) =>
  pubs.find((p) => p.type === type && p.business_date === date) || null;

const BpkdPkdBrowser = ({
  deliveryDate, // the table's resolved delivery date (today / tomorrow / calendar)
  bpkdSelected, // BPKD_SELECTED — latest BPKD for deliveryDate ("Last")
  pkdSelected, // PKD_SELECTED — latest PKD for deliveryDate ("#2")
  onSetBpkd,
  onSetPkd,
}) => {
  const { fetchGet, isAuthenticated } = useApi();

  const [publications, setPublications] = useState([]);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [viewerPacks, setViewerPacks] = useState(null); // packs shown in the popup (1=view, >1=compare)
  const [compareKeys, setCompareKeys] = useState([]); // ordered pubKeys selected for comparison

  // Auto-follow: while on, the selection tracks the newest pack of its type.
  // It is switched off only when the user explicitly picks an entry; Reset
  // turns it back on (and snaps to the current latest).
  const [autoLast, setAutoLast] = useState(true);
  const [autoPkd, setAutoPkd] = useState(true);

  // Refs so the polling callback stays stable yet reads current flag/handler.
  const autoLastRef = useRef(autoLast);
  const autoPkdRef = useRef(autoPkd);
  const setBpkdRef = useRef(onSetBpkd);
  const setPkdRef = useRef(onSetPkd);
  useEffect(() => {
    autoLastRef.current = autoLast;
  }, [autoLast]);
  useEffect(() => {
    autoPkdRef.current = autoPkd;
  }, [autoPkd]);
  useEffect(() => {
    setBpkdRef.current = onSetBpkd;
    setPkdRef.current = onSetPkd;
  }, [onSetBpkd, onSetPkd]);

  const load = useCallback(async () => {
    if (!deliveryDate) return;
    try {
      // Always scope the browser to the chosen delivery date's packs.
      const res = await fetchGet(
        `/pse/publications?date=${encodeURIComponent(deliveryDate)}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const pubs = Array.isArray(data.publications) ? data.publications : [];
      setPublications(pubs);
      setError(null);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLastUpdate(new Date().toLocaleTimeString());
    }
  }, [fetchGet, deliveryDate]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [isAuthenticated, load]);

  // Auto-selection is scoped to the chosen delivery date. Re-runs whenever the
  // list refreshes or the delivery date changes (e.g. Today -> Tomorrow). When
  // no pack of a type exists for that date, the selection is cleared (blank).
  useEffect(() => {
    if (autoLastRef.current) {
      setBpkdRef.current(newestForDate(publications, "BPKD", deliveryDate));
    }
    if (autoPkdRef.current) {
      setPkdRef.current(newestForDate(publications, "PKD", deliveryDate));
    }
  }, [publications, deliveryDate]);

  // Comparison selection only makes sense within one delivery date.
  useEffect(() => {
    setCompareKeys([]);
  }, [deliveryDate]);

  // Explicit pick — freeze auto for that role.
  const handleSetLast = (p) => {
    setAutoLast(false);
    onSetBpkd(p);
  };
  const handleSetPkd = (p) => {
    setAutoPkd(false);
    onSetPkd(p);
  };

  // Comparison: toggle a pack in/out of the ordered compare set; open the viewer.
  const toggleCompare = (p) => {
    const key = pubKey(p);
    setCompareKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
  const openCompare = () => {
    const chosen = compareKeys
      .map((k) => publications.find((pp) => pubKey(pp) === k))
      .filter(Boolean);
    if (chosen.length >= 2) setViewerPacks(chosen);
  };

  // Reset / enable auto — snap to the latest of the type for the delivery date
  // and keep auto-upgrading from here.
  const enableAutoLast = () => {
    setAutoLast(true);
    onSetBpkd(newestForDate(publications, "BPKD", deliveryDate));
  };
  const enableAutoPkd = () => {
    setAutoPkd(true);
    onSetPkd(newestForDate(publications, "PKD", deliveryDate));
  };
  const toggleAutoLast = () => (autoLast ? setAutoLast(false) : enableAutoLast());
  const toggleAutoPkd = () => (autoPkd ? setAutoPkd(false) : enableAutoPkd());

  // Green "<name> Auto" while auto-following; slight red "<name> Manual" once a
  // pack has been picked by hand. Click toggles between the two.
  const pill = (active, name, onClick) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 text-xs rounded-full border transition ${
        active
          ? "bg-green-100 text-green-700 border-green-300 hover:bg-green-200"
          : "bg-red-100 text-red-700 border-red-300 hover:bg-red-200"
      }`}
    >
      {active ? `${name} Auto` : `${name} Manual`}
    </button>
  );

  return (
    <div className="w-full md:w-1/2 rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">BPKD &amp; PKD browser</h3>
        <div className="flex items-center gap-2">
          {pill(autoPkd, "PKD", toggleAutoPkd)}
          {pill(autoLast, "Last", toggleAutoLast)}
          <button
            type="button"
            onClick={openCompare}
            disabled={compareKeys.length < 2}
            className={`px-2.5 py-1 text-xs rounded-full border transition ${
              compareKeys.length < 2
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-sky-500 text-white border-sky-500 hover:bg-sky-600"
            }`}
          >
            Compare{compareKeys.length ? ` (${compareKeys.length})` : ""}
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="p-1 rounded text-gray-500 hover:bg-gray-100"
            aria-label={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? <Plus className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* List */}
          <div className="p-3 space-y-2 max-h-[420px] overflow-y-auto">
            {error && (
              <div className="text-xs text-red-600 px-1 py-2">Error: {error}</div>
            )}
            {!error && publications.length === 0 && (
              <div className="text-xs text-gray-500 px-1 py-2">
                {`No publications for ${deliveryDate}.`}
              </div>
            )}
            {publications.map((p) => {
              const isLast = samePub(p, bpkdSelected);
              const isPkd = samePub(p, pkdSelected);
              const compareIdx = compareKeys.indexOf(pubKey(p));
              // Per-type background tint (dark equivalents in index.css).
              const typeStyle =
                p.type === "PKD"
                  ? "bg-violet-50 border-violet-200"
                  : "bg-sky-50 border-sky-200";
              return (
                <div
                  key={pubKey(p)}
                  data-type={p.type}
                  className={`pub-card flex items-center gap-4 rounded-lg border px-4 py-3 ${typeStyle} ${
                    isLast || isPkd ? "ring-2 ring-blue-400" : ""
                  }`}
                >
                  <span className="text-xs font-bold text-gray-700 w-12 shrink-0">
                    {p.type}
                  </span>
                  <div className="text-xs text-gray-600 leading-5 flex-1 min-w-0">
                    <div>Created at: {p.created_at}</div>
                    <div>Delivery date: {p.business_date}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isPkd ? (
                      <span className="px-2.5 py-1 text-xs rounded bg-green-100 text-green-700">
                        #2
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSetPkd(p)}
                        className="px-3 py-1.5 text-xs rounded bg-green-500 text-white hover:bg-green-600"
                      >
                        Set as #2
                      </button>
                    )}
                    {isLast ? (
                      <span className="px-2.5 py-1 text-xs rounded bg-blue-100 text-blue-700">
                        Last
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSetLast(p)}
                        className="px-3 py-1.5 text-xs rounded bg-green-500 text-white hover:bg-green-600"
                      >
                        Set as Last
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setViewerPacks([p])}
                      className="p-1.5 rounded text-gray-500 hover:bg-gray-100"
                      title="View all pack data"
                      aria-label="View all pack data"
                    >
                      <Binoculars className="w-4 h-4" />
                    </button>
                    {compareIdx >= 0 ? (
                      <button
                        type="button"
                        onClick={() => toggleCompare(p)}
                        className="w-7 h-7 flex items-center justify-center text-xs font-semibold rounded bg-sky-600 text-white"
                        title="Remove from comparison"
                      >
                        #{compareIdx + 1}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleCompare(p)}
                        className="p-1.5 rounded text-gray-500 hover:bg-gray-100"
                        title="Add to comparison"
                        aria-label="Add to comparison"
                      >
                        <GitCompare className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {viewerPacks ? (
            <PackDetailModal packs={viewerPacks} onClose={() => setViewerPacks(null)} />
          ) : null}

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={enableAutoLast}
                className="px-3 py-1.5 text-xs rounded bg-orange-500 text-white hover:bg-orange-600"
              >
                Reset Last
              </button>
              <button
                type="button"
                onClick={enableAutoPkd}
                className="px-3 py-1.5 text-xs rounded bg-orange-500 text-white hover:bg-orange-600"
              >
                Reset #2
              </button>
            </div>
            <span className="text-xs text-gray-500">
              {lastUpdate ? `Last update: ${lastUpdate}` : "Loading…"}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default BpkdPkdBrowser;
